import React, { useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { pdfjs } from 'react-pdf';
import { Font } from '@react-pdf/renderer';
import { PopUpNotification } from 'src/interface';
import { AUTH } from 'src/constants';
import {
  Context,
  useContextData,
  useContextHandler,
  useNavigation,
  usePage,
} from 'src/hooks';
import { useAccountState } from 'src/services/auth';
import { PopUpNotifications } from 'src/components/general';
import Header from 'src/components/header/header';
import ErrorBoundary from '../errorBoundary/errorBoundary';
import styles from './layoutStyle.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc =
  window.location.origin + '/scripts/pdf.worker.min.mjs';

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: '/fonts/Inter-Thin.ttf',
      fontWeight: 100,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-ThinItalic.ttf',
      fontWeight: 100,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-ExtraLight.ttf',
      fontWeight: 200,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-ExtraLightItalic.ttf',
      fontWeight: 200,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-Light.ttf',
      fontWeight: 300,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-LightItalic.ttf',
      fontWeight: 300,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-Regular.ttf',
      fontWeight: 400,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-RegularItalic.ttf',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-Medium.ttf',
      fontWeight: 500,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-MediumItalic.ttf',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-SemiBold.ttf',
      fontWeight: 600,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-SemiBoldItalic.ttf',
      fontWeight: 600,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-Bold.ttf',
      fontWeight: 700,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-BoldItalic.ttf',
      fontWeight: 700,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-ExtraBold.ttf',
      fontWeight: 800,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-ExtraBoldItalic.ttf',
      fontWeight: 800,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter-Black.ttf',
      fontWeight: 900,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter-BlackItalic.ttf',
      fontWeight: 900,
      fontStyle: 'italic',
    },
  ],
});
Font.registerHyphenationCallback((word) => {
  return word.length > 8 ? word.split('') : [word];
});

const ContextWrapper = () => {
  const { user, isPending } = useAccountState();
  const {
    layoutScroller: { trigger, scroll },
  } = useContextData();
  const { pathname } = useLocation();
  const { page } = usePage();
  useNavigation();

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.scrollTo(trigger.x, trigger.y);
  }, [trigger]);

  useEffect(() => {
    scroll.call(0, 0);
  }, [pathname, scroll]);

  const notificationsFilter = useCallback(
    ({ title }: PopUpNotification) =>
      (page && ![AUTH].includes(page)) ||
      !['error.AccountTokenExpiredError'].includes(title),
    [page],
  );

  if (isPending) {
    return null;
  }

  return (
    <div className={styles.container} ref={ref}>
      <PopUpNotifications filter={notificationsFilter} />
      {user && <Header />}
      <div className={styles.content}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </div>
    </div>
  );
};

const Layout = () => {
  const handler = useContextHandler();

  return (
    <Context.Provider value={handler}>
      <ContextWrapper />
    </Context.Provider>
  );
};

export default Layout;
